<template>
  <div class="transcript-page">
    <div class="no-print actions" style="margin-bottom:20px">
      <button type="button" class="btn btn-outline" @click="$router.back()">{{ t.back }}</button>
      <button type="button" class="btn btn-outline" :disabled="!report || downloading" @click="downloadPdf">
        {{ downloading ? t.preparingPdf : t.downloadPdf }}
      </button>
      <a
        v-if="report && report.reportMeta && report.reportMeta.verifyUrl"
        class="btn btn-outline"
        :href="report.reportMeta.verifyUrl"
        target="_blank"
        rel="noopener"
      >{{ t.openVerify }}</a>
    </div>
    <div v-if="isInstitutionView && report" class="inst-banner no-print">{{ t.institutionBanner }}</div>
    <dsa-loading v-if="loading" :text="t.loading" />
    <div v-else-if="!report" class="empty">{{ t.unavailable }}</div>
    <div v-else class="official-report">
      <header class="r-header">
        <div class="doc-kicker">{{ t.officialTranscript }}</div>
        <div class="brand-name">{{ t.brandName }}</div>
        <div class="brand-sub">{{ t.brandSub }}</div>
      </header>

      <section class="cand-grid">
        <div class="cand-col">
          <div class="cand-row">
            <span class="lbl">{{ t.candidateName }}:</span>
            <span class="val">{{ report.candidate.name }}</span>
          </div>
          <div class="cand-row">
            <span class="lbl">{{ t.email }}:</span>
            <span class="val">{{ report.candidate.email }}</span>
          </div>
          <div class="cand-row">
            <span class="lbl">{{ t.examId }}:</span>
            <span class="val">{{ report.candidate.examId }}</span>
          </div>
        </div>
        <div class="cand-col">
          <div class="cand-row">
            <span class="lbl">{{ t.dobShort }}:</span>
            <span class="val">{{ report.candidate.dob }}</span>
          </div>
          <div class="cand-row">
            <span class="lbl">{{ t.gender }}:</span>
            <span class="val">{{ report.candidate.gender }}</span>
          </div>
        </div>
      </section>

      <section class="subject-hero">
        <h1 class="subject-title">{{ report.subject.name }}</h1>
        <p class="subject-meta">
          {{ t.discipline }}: {{ report.subject.discipline }}
          <span class="sep">|</span>
          {{ t.subjectCode }}: {{ report.subject.code }}
        </p>
      </section>

      <section class="score-hero">
        <div class="hero-label">{{ t.consolidatedScore }}</div>
        <div class="hero-score">
          <span class="hero-num">{{ format1(report.score.consolidated) }}</span>
          <span class="hero-den">/ {{ report.score.fullScore || 120 }}</span>
        </div>
        <div v-if="report.score.performanceLevel" class="level-line">
          {{ t.performanceLevel }}:
          <strong>{{ String(report.score.performanceLevel).toUpperCase() }}</strong>
        </div>
      </section>

      <section class="r-section">
        <h2>{{ t.scoreBreakdown }}</h2>
        <ul class="breakdown">
          <li v-for="(row, i) in displayBreakdown" :key="'b-' + i">
            <span class="b-label">{{ row.label }}</span>
            <div class="b-bar-wrap" :class="{ 'has-bar': i === 0 }" aria-hidden="true">
              <div v-if="i === 0" class="b-bar" :style="{ width: scoreBarPct + '%' }"></div>
            </div>
            <b class="b-val">{{ row.value }}</b>
          </li>
        </ul>
      </section>

      <section class="r-section">
        <h2>{{ t.assessmentHistory }}</h2>
        <div class="table-wrap">
          <table class="hist">
            <thead>
              <tr>
                <th>{{ t.date }}</th>
                <th>{{ t.testingWindow }}</th>
                <th>{{ t.attempt }}</th>
                <th>{{ t.raw }}</th>
                <th>{{ t.windowBonus }}</th>
                <th>{{ t.headroom }}</th>
                <th>{{ t.effectiveBonus }}</th>
                <th>{{ t.credit }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in report.attempts" :key="'a-' + a.attemptNo">
                <td>{{ a.date || '—' }}</td>
                <td>{{ a.window }}</td>
                <td>{{ a.attemptLabel || ordinal(a.attemptNo) }}</td>
                <td>{{ format1(a.raw) }}</td>
                <td class="bonus">{{ signed1(a.windowBonus) }}</td>
                <td>{{ a.headroom }}</td>
                <td class="bonus">{{ signed1(a.effBonus) }}</td>
                <td>{{ a.credit }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="r-section">
        <h2>{{ t.scoreCalculation }}</h2>
        <ul class="pathways">
          <li
            v-for="p in report.pathways"
            :key="'p-' + p.n"
            :class="{ selected: p.selected }"
          >
            <span class="p-label">{{ p.label || ('Pathway ' + p.n) }}:</span>
            <span class="p-formula">{{ p.formula }}</span>
            <span v-if="p.selected" class="sel-tag">← {{ t.selectedHighest }}</span>
          </li>
        </ul>
      </section>

      <section v-if="report.scale && report.scale.bands" class="r-section">
        <h2>{{ t.performanceScale }}</h2>
        <div class="scale">
          <div
            v-for="(band, i) in report.scale.bands"
            :key="band.name"
            class="band"
            :class="['tone-' + i, { hit: band.hit }]"
          >
            <div class="band-name">{{ band.name }}</div>
            <div class="band-range">{{ band.range }}</div>
          </div>
        </div>
      </section>

      <section class="verify-cert">
        <div class="vc-col">
          <h2>{{ t.certification }}</h2>
          <dl class="verify-list">
            <div><dt>{{ t.reportId }}</dt><dd>{{ report.reportMeta.reportId }}</dd></div>
            <div><dt>{{ t.issueDate }}</dt><dd>{{ report.reportMeta.issueDate }}</dd></div>
            <div><dt>{{ t.validUntil }}</dt><dd>{{ report.reportMeta.validUntil }}</dd></div>
          </dl>
          <p class="cert-stmt">{{ displayCert.statement }}</p>
        </div>
        <div class="vc-col vc-verify">
          <h2>{{ t.verification }}</h2>
          <div class="qr-block">
            <img
              v-if="qrDataUrl"
              class="verify-qr-img"
              :src="qrDataUrl"
              width="140"
              height="140"
              alt="Scan to Verify"
            >
            <div v-else class="qr-fallback">{{ qrBusy ? '…' : 'QR' }}</div>
            <div class="qr-caption">{{ t.scanToVerify }}</div>
          </div>
        </div>
      </section>

      <section class="r-section notes-sec">
        <h2>{{ t.notes }}</h2>
        <p class="notes-inline">
          <template v-for="(n, i) in displayNotes">
            <span :key="'n-' + i">{{ i + 1 }}. {{ n }} </span>
          </template>
        </p>
        <p v-if="report.footer && report.footer.policyUrl" class="policy-link">
          {{ t.policyVisit }}:
          <a :href="report.footer.policyUrl" target="_blank" rel="noopener">{{ report.footer.policyUrl }}</a>
        </p>
        <p v-if="displayFooter.legal" class="legal-notice">{{ displayFooter.legal }}</p>
      </section>

      <footer class="r-footer">
        <p class="footer-meta">
          This document is issued by Deep Scholastic Assessment (DSA).
          Verification: {{ report.reportMeta.verifyHost || report.reportMeta.verifyUrl }}
          | Code: {{ report.footer.reportCode }}
        </p>
        <p class="footer-copy">{{ displayFooter.copyright }}</p>
      </footer>
    </div>
  </div>
</template>

<script>
import { getScoreReport, getTranscriptForAttempt, downloadScoreReportPdf } from '@/api/dsa'
import { getInstitutionAccountId, getUserId } from '@/utils/store'
import QRCode from 'qrcode'
import {
  scoreReportCopy,
} from '@/i18n/scoreReport'

export default {
  name: 'StudentTranscript',
  data() {
    return {
      loading: true,
      downloading: false,
      report: null,
      locale: 'en',
      qrDataUrl: '',
      qrBusy: false,
    }
  },
  computed: {
    t() {
      return scoreReportCopy(this.locale)
    },
    isInstitutionView() {
      return this.$route.path.includes('/institution/')
    },
    scoreBarPct() {
      if (!this.report || !this.report.score) return 0
      const full = Number(this.report.score.fullScore) || 120
      const csc = Number(this.report.score.consolidated) || 0
      return Math.max(0, Math.min(100, (csc / full) * 100))
    },
    displayBreakdown() {
      const rows = (this.report && this.report.breakdown) || []
      return rows.filter((row) => {
        const label = String((row && row.label) || '')
        return !/Difficulty|\bDI\b/i.test(label)
      })
    },
    displayNotes() {
      if (!this.report) return []
      const raw = this.report.notes || []
      return raw.filter((note) => {
        const text = String(note || '')
        return !/Difficulty Index|card index|\bDI\b|难度指数/i.test(text)
      })
    },
    displayCert() {
      if (!this.report) return {}
      return this.report.certification || {}
    },
    displayFooter() {
      if (!this.report) return {}
      return this.report.footer || {}
    },
    verifyUrl() {
      if (!this.report || !this.report.reportMeta) return ''
      const meta = this.report.reportMeta
      if (meta.verifyUrl) return String(meta.verifyUrl)
      const id = meta.reportId
      if (!id) return ''
      const front = (process.env.VUE_APP_DSA_FRONT_URL || 'https://my.dsaexam.com').replace(/\/$/, '')
      return `${front}/#/verify?report_id=${encodeURIComponent(id)}`
    },
  },
  watch: {
    verifyUrl: {
      immediate: true,
      handler(url) {
        this.buildQr(url)
      },
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async buildQr(url) {
      const text = String(url || '').trim()
      this.qrDataUrl = ''
      if (!text) return
      this.qrBusy = true
      try {
        this.qrDataUrl = await QRCode.toDataURL(text, {
          width: 140,
          margin: 2,
          errorCorrectionLevel: 'M',
          color: { dark: '#111111', light: '#ffffff' },
        })
      } catch (e) {
        this.qrDataUrl = ''
      } finally {
        this.qrBusy = false
      }
    },
    async ensureQrDataUrl() {
      if (this.qrDataUrl) return this.qrDataUrl
      if (!this.verifyUrl) return ''
      await this.buildQr(this.verifyUrl)
      return this.qrDataUrl || ''
    },
    format1(n) {
      if (n == null || n === '') return '—'
      const x = Number(n)
      if (Number.isNaN(x)) return String(n)
      return x.toFixed(1)
    },
    signed1(n) {
      if (n == null || n === '') return '—'
      const x = Number(n)
      if (Number.isNaN(x)) return String(n)
      const s = x.toFixed(1)
      return x >= 0 ? `+${s}` : s
    },
    ordinal(n) {
      if (n === 1) return '1st'
      if (n === 2) return '2nd'
      if (n === 3) return '3rd'
      return `${n}th`
    },
    async downloadPdf() {
      const applicationId = this.$route.params.applicationId
      if (!applicationId || !this.report) return
      this.downloading = true
      try {
        const account_id = this.isInstitutionView
          ? getInstitutionAccountId()
          : null
        const res = await downloadScoreReportPdf({
          application_id: applicationId,
          student_id: getUserId() || undefined,
          account_id: account_id || undefined,
          qr_data_url: this.qrDataUrl || (await this.ensureQrDataUrl()),
        })
        if (!(res.data && res.data.code === 0 && res.data.data && res.data.data.pdf_base64)) {
          this.$emit('toast', (res.data && res.data.msg) || this.t.couldNotPdf)
          return
        }
        const bin = atob(res.data.data.pdf_base64)
        const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        const blob = new Blob([bytes], { type: res.data.data.mime || 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = res.data.data.filename || 'DSA-Transcript.pdf'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        this.$emit('toast', this.t.couldNotDownload)
      } finally {
        this.downloading = false
      }
    },
    async load() {
      const applicationId = this.$route.params.applicationId
      if (!applicationId) {
        this.loading = false
        return
      }
      try {
        const account_id = this.isInstitutionView
          ? getInstitutionAccountId()
          : null
        const student_id = getUserId() || ''
        let res = await getScoreReport({
          application_id: applicationId,
          student_id: student_id || undefined,
          account_id: account_id || undefined,
          lang: this.locale,
        })
        if (res.data && res.data.code === 0 && res.data.data && res.data.data.official) {
          this.report = res.data.data
        } else {
          res = await getTranscriptForAttempt(applicationId, account_id)
          if (res.data && res.data.code === 0 && res.data.data && res.data.data.official) {
            this.report = res.data.data
          } else {
            this.$emit('toast', (res.data && res.data.msg) || this.t.couldNotLoad)
          }
        }
      } catch (e) {
        this.$emit('toast', this.t.couldNotLoad)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.transcript-page{max-width:880px;margin:0 auto}
.actions{display:flex;flex-wrap:wrap;gap:10px;align-items:center}
.inst-banner{
  margin-bottom:14px;padding:10px 14px;border-radius:4px;
  background:#f5f7fa;border:1px solid #d8e2ec;color:#1a3a5c;font-size:13px;
}
.official-report{
  --navy:#1a3a5c;
  --green:#1b5e20;
  --green2:#2e7d32;
  --muted-ink:#666;
  --score-bg:#f5f7fa;
  --bar:#e8eef4;
  background:#fff;
  border:1px solid #d0d7e0;
  padding:32px 36px 24px;
  color:#222;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
}
.r-header{text-align:center;margin-bottom:16px}
.doc-kicker{
  font-size:8px;font-weight:400;letter-spacing:.08em;color:#999;text-transform:uppercase;
  text-align:right;
}
.brand-name{margin-top:14px;font-size:16px;font-weight:800;letter-spacing:.03em;color:var(--navy)}
.brand-sub{margin-top:10px;font-size:11px;color:var(--muted-ink)}
.cand-grid{
  display:grid;grid-template-columns:1.4fr 1fr;gap:8px 24px;margin-bottom:16px;
  padding-top:4px;padding-bottom:28px;border-bottom:1px solid #ddd;
}
.cand-row{display:grid;grid-template-columns:110px 1fr;gap:8px;font-size:12px;line-height:1.6;align-items:baseline}
.cand-row .lbl{color:var(--muted-ink);font-weight:400;font-size:11px}
.cand-row .val{color:#111;font-weight:600;font-size:12px;text-align:left}
.subject-hero{text-align:center;margin:4px 0 24px}
.subject-title{
  margin:0;font-size:24px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:var(--navy);
}
.subject-meta{margin:14px 0 0;font-size:13px;color:var(--muted-ink)}
.subject-meta .sep{margin:0 8px;color:#bbb}
.score-hero{
  text-align:center;padding:18px 16px 26px;margin-bottom:20px;
  border:1px solid #d8e2ec;background:var(--score-bg);border-radius:2px;
}
.hero-label{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--muted-ink);font-weight:700}
.hero-score{margin-top:8px;line-height:1}
.hero-num{font-size:36px;font-weight:800;color:var(--navy)}
.hero-den{font-size:15px;color:var(--muted-ink);margin-left:4px;font-weight:600;vertical-align:baseline}
.level-line{margin-top:12px;margin-bottom:4px;font-size:12px;color:#333}
.level-line strong{color:var(--green);font-weight:800;margin-left:4px}
.r-section{margin-bottom:20px}
.r-section h2,.vc-col h2{
  font-size:12px;text-transform:uppercase;letter-spacing:.1em;color:var(--navy);
  margin:0 0 16px;font-weight:800;padding-bottom:14px;border-bottom:none;
}
.breakdown{list-style:none;padding:0;margin:0}
.breakdown li{
  display:grid;grid-template-columns:160px 1fr auto;gap:12px;align-items:center;
  padding:8px 0;border-bottom:1px solid #eee;font-size:13px;
}
.breakdown li:last-child{border-bottom:none}
.b-label{color:#333}
.b-bar-wrap{height:10px;background:transparent;border-radius:2px;overflow:hidden}
.b-bar-wrap.has-bar{background:var(--bar)}
.b-bar{height:100%;background:var(--navy)}
.b-val{color:var(--navy);font-weight:800;text-align:right;justify-self:end;grid-column:3}
.table-wrap{overflow-x:auto}
.hist{width:100%;border-collapse:collapse;font-size:12px;border:1px solid #bfbfbf}
.hist th{
  padding:8px 6px;text-align:center;white-space:nowrap;font-size:10px;
  text-transform:uppercase;letter-spacing:.04em;background:var(--navy);color:#fff;font-weight:700;
  border:1px solid #1a3a5c;
}
.hist td{
  padding:8px 6px;border:1px solid #bfbfbf;white-space:nowrap;color:#222;text-align:center;
}
.hist tbody tr:nth-child(even) td{background:#fafafa}
.hist td.bonus{color:var(--green2);font-weight:700}
.pathways{
  list-style:none;padding:10px 12px;margin:0;display:grid;gap:8px;
  background:#f0faf0;border:1px solid #d4ebd4;border-radius:2px;font-size:13px;
}
.pathways li.selected{color:var(--green);font-weight:700}
.p-label{margin-right:6px}
.p-formula{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:12.5px}
.sel-tag{margin-left:8px;font-size:12px;font-weight:800}
.scale{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:0;overflow:hidden;border-radius:2px}
.band{
  padding:0 8px;text-align:center;color:#fff;height:64px;
  display:flex;flex-direction:column;justify-content:center;align-items:center;gap:4px;
  box-sizing:border-box;
}
.band.tone-0{background:#1b5e20}
.band.tone-1{background:#2e7d32}
.band.tone-2{background:#fbc02d;color:#333}
.band.tone-3{background:#ff9800}
.band.tone-4{background:#e53935}
.band.hit{box-shadow:inset 0 0 0 2px rgba(0,0,0,.35)}
.band-name{font-weight:800;font-size:12px;line-height:1.2;margin:0}
.band-range{font-size:11px;line-height:1.2;margin:0;opacity:.95}
.verify-cert{
  display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px;
  padding-top:4px;
}
.vc-col{min-width:0}
.vc-verify{text-align:center}
.verify-list{margin:0;display:grid;gap:8px;font-size:13px}
.verify-list > div{display:grid;grid-template-columns:96px 1fr;gap:8px}
.verify-list dt{margin:0;font-weight:600;color:var(--muted-ink)}
.verify-list dd{margin:0;font-weight:700;color:#111;word-break:break-word}
.cert-stmt{font-size:13px;line-height:1.5;margin:14px 0 0;color:#333}
.qr-block{display:flex;flex-direction:column;align-items:center;padding-top:4px}
.verify-qr-img{
  width:140px;height:140px;display:block;object-fit:contain;
  border:1px solid #e5eaf0;background:#fff;padding:6px;box-sizing:content-box;
}
.qr-fallback{
  width:140px;height:140px;display:flex;align-items:center;justify-content:center;
  border:1px solid #ccc;color:#999;font-size:12px;background:#fff;
}
.qr-caption{margin-top:10px;font-size:12px;font-weight:700;color:var(--navy);letter-spacing:.02em}
.r-footer{
  margin-top:18px;padding-top:12px;border-top:1px solid #ccc;
  font-size:10px;color:#777;line-height:1.5;
}
.footer-meta,.footer-copy{margin:4px 0 0;text-align:center}
.footer-copy{font-size:9px;color:#999}
.notes-inline{margin:0;font-size:12px;line-height:1.55;color:#555}
.policy-link{margin:10px 0 0;font-size:12px}
.policy-link a{color:var(--navy);word-break:break-all}
.legal-notice{
  margin:12px 0 0;font-size:10px;line-height:1.45;color:#999;font-style:italic;
}
@media (max-width:800px){
  .cand-grid,.verify-cert{grid-template-columns:1fr}
  .scale{grid-template-columns:1fr 1fr}
  .breakdown li{grid-template-columns:1fr;gap:6px}
  .b-bar-wrap{order:3}
  .hero-num{font-size:40px}
  .official-report{padding:20px 16px}
  .subject-title{font-size:20px}
}
@media print{
  .no-print{display:none!important}
  .transcript-page{max-width:none;padding:0}
  .official-report{border:none;padding:0}
}
</style>
